import React, { useState, useRef, useEffect } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CFormInput,
  CAlert,
  CSpinner
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilShieldAlt, cilReload } from '@coreui/icons';

const SecureCaptcha = ({ 
  show, 
  onSuccess, 
  onClose, 
  redirectUrl, 
  title = "Security Verification",
  description = "Please complete the security check to proceed to payment."
}) => {
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let text = '';
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(text);
    setUserInput('');
    setError('');
  };

  const drawCaptcha = () => {
    const canvas = canvasRef.current;
    if (!canvas || !captchaText) return;

    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.3)`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        Math.random() * 2,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
    
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.3)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
    
    ctx.font = 'bold 24px Arial';
    ctx.fillStyle = '#2C5AA0';
    
    for (let i = 0; i < captchaText.length; i++) {
      ctx.save();
      
      const x = 20 + i * 25;
      const y = 35;
      
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.4);
      
      ctx.fillText(captchaText[i], 0, 0);
      
      ctx.restore();
    }
  };

  const handleVerify = () => {
    if (!userInput.trim()) {
      setError('Please enter the captcha code');
      return;
    }

    // FIXED: Case-sensitive comparison
    if (userInput !== captchaText) {
      setError('Invalid captcha code. Please try again.');
      generateCaptcha();
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      setLoading(false);
      onSuccess(redirectUrl);
    }, 1000);
  };

  const handleClose = () => {
    setUserInput('');
    setError('');
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        generateCaptcha();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [show]);

  useEffect(() => {
    if (captchaText) {
      const timer = setTimeout(() => {
        drawCaptcha();
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [captchaText]);

  useEffect(() => {
    if (show && canvasRef.current) {
      const timer = setTimeout(() => {
        if (captchaText) {
          drawCaptcha();
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [show, captchaText]);

  return (
    <CModal 
      visible={show} 
      onClose={handleClose}
      backdrop="static"
      keyboard={false}
      onShow={() => {
        setTimeout(() => {
          if (captchaText && canvasRef.current) {
            drawCaptcha();
          }
        }, 200);
      }}
    >
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilShieldAlt} className="me-2 text-primary" />
          {title}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <div className="text-center">
          <p>{description}</p>
          
          <div className="mb-3 p-3 border rounded bg-light">
            <canvas 
              ref={canvasRef} 
              width={200} 
              height={60}
              className="border rounded"
              key={captchaText}
            />
            <div className="mt-2">
              <CButton 
                color="outline-secondary" 
                size="sm" 
                onClick={generateCaptcha}
                disabled={loading}
              >
                <CIcon icon={cilReload} className="me-1" />
                Refresh Code
              </CButton>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="captchaInput" className="form-label">
              Enter the code shown above (case-sensitive):
            </label>
            <CFormInput
              id="captchaInput"
              type="text"
              placeholder="Type the code here..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              autoComplete="off"
              autoFocus
            />
          </div>

          {error && (
            <CAlert color="danger" className="py-2">
              {error}
            </CAlert>
          )}

          {loading && (
            <div className="my-3">
              <CSpinner size="sm" className="me-2" />
              Verifying...
            </div>
          )}
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton 
          color="secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </CButton>
        <CButton 
          color="primary" 
          onClick={handleVerify}
          disabled={loading}
        >
          {loading ? 'Verifying...' : 'Verify & Continue'}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default SecureCaptcha;